import { useState } from "react";
import { Checkbox, NativeSelect, TextInput } from "@mantine/core";
import { useListParams } from "../lib/useListParams";
import { sortRows } from "../lib/sortRows";
import { Badge, type Tone } from "../ui/Badge";
import { ActionTile, ActionTiles } from "../ui/ActionTile";
import { Button } from "../ui/Button";
import { IconClipboard, IconDownload, IconGlobe, IconNotes } from "../ui/icons";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { Drawer } from "../ui/Drawer";
import { Field, Section } from "../ui/Fields";
import { RowMenu } from "../ui/Menu";
import { Spinner } from "../components/Spinner";

/* Every shared piece in every state, on one page, for an admin to review
 * before a screen uses it. Rendered at whatever width the window is, so a
 * reviewer resizes it or uses the browser's device mode to see the narrow
 * layouts. docs/plans/front-end-overhaul.md, The styleguide route.
 *
 * Nothing here reads the API. The rows are made up and say so. */

const TOKENS: { name: string; job: string }[] = [
  { name: "--page", job: "Page and table background" },
  { name: "--surface", job: "Panels, the filter area, the open row" },
  { name: "--surface-strong", job: "Row hover, a pressed segment" },
  { name: "--text", job: "Body text, table cells" },
  { name: "--text-muted", job: "Labels, secondary text" },
  { name: "--heading", job: "Headings, links, the current page" },
  { name: "--line", job: "Rules between rows" },
  { name: "--control-edge", job: "Borders of inputs and buttons" },
  { name: "--bar", job: "The top bar" },
  { name: "--action", job: "The orange button" },
  { name: "--ok", job: "Found, done" },
  { name: "--waiting", job: "Queued, running" },
  { name: "--refusal", job: "Refused, failed" },
  { name: "--notice-bg", job: "A notice under the bar" },
];

const TONES: { tone: Tone; word: string }[] = [
  { tone: "ok", word: "Found" },
  { tone: "waiting", word: "Waiting" },
  { tone: "refusal", word: "Failed" },
  { tone: "quiet", word: "Not searched" },
  { tone: "plain", word: "Owner" },
];

type Sample = { id: string; name: string; city: string; state: string; properties: number | null; people: number; status: Tone; word: string };

const SAMPLE: Sample[] = [
  { id: "1", name: "Example Residential", city: "Phoenix", state: "AZ", properties: 492, people: 38, status: "ok", word: "Found" },
  { id: "2", name: "Sample Property Group", city: "Denver", state: "CO", properties: 61, people: 9, status: "waiting", word: "Waiting" },
  { id: "3", name: "Placeholder Capital", city: "Newark", state: "NJ", properties: null, people: 2, status: "quiet", word: "Not searched" },
  { id: "4", name: "Made-up Management", city: "Oakland", state: "CA", properties: 7, people: 0, status: "refusal", word: "Failed" },
];

const SAMPLE_COLUMNS: ColumnSpec<Sample>[] = [
  { id: "name", header: "Name", role: "leading", fields: ["name"], sort: "name", narrowLabel: null,
    cell: (r) => <button type="button" className="link">{r.name}</button> },
  { id: "city", header: "City", role: "leading", fields: ["city"], sort: "city", narrowLabel: null, cell: (r) => r.city },
  { id: "state", header: "State", role: "leading", fields: ["state"], sort: "state", narrowLabel: null, cell: (r) => r.state },
  { id: "properties", header: "Properties", role: "secondary", fields: ["properties"], align: "num", sort: "properties",
    cell: (r) => r.properties === null ? <span className="muted" title="Added since the last count">-</span> : r.properties.toLocaleString() },
  { id: "people", header: "People", role: "secondary", fields: ["people"], align: "num", sort: "people", cell: (r) => r.people.toLocaleString() },
  { id: "status", header: "Enrichment", role: "secondary", fields: [], narrowLabel: null, cell: (r) => <Badge tone={r.status}>{r.word}</Badge> },
  { id: "detail", header: "A detail field", role: "detail", fields: ["detail"], cell: () => "In the record only" },
];

export function Styleguide() {
  const params = useListParams("properties", [], false, "", "sg.");
  const [open, setOpen] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);

  const rows = sortRows(SAMPLE, params.sorts, (r, column) => {
    const v = r[column as keyof Sample];
    return typeof v === "number" || typeof v === "string" ? v : null;
  });

  return (
    <main className="sg">
      <h1>Styleguide</h1>
      <p className="muted">The shared pieces, each in its states. The rows below are made up.</p>

      <section>
        <h2>Colors</h2>
        <div className="sg-swatches">
          {TOKENS.map((t) => (
            <div key={t.name} className="sg-swatch">
              <div className="sg-swatch-color" style={{ background: `var(${t.name})` }} />
              <div className="sg-swatch-name"><code>{t.name}</code><br /><span className="muted">{t.job}</span></div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>Type</h2>
        <h1>A first-level heading at 1.25rem</h1>
        <h2>A second-level heading at 1.05rem</h2>
        <h3>A third-level heading at 0.95rem</h3>
        <p>Body text at 14px in Plus Jakarta Sans. <span className="muted">Muted text for labels and secondary values.</span> Figures in a table column line up: <span className="num" style={{ fontVariantNumeric: "tabular-nums" }}>1,234 and 5,678</span>.</p>
      </section>

      <section>
        <h2>Buttons</h2>
        <div className="sg-row">
          <Button kind="orange">Run the campaign</Button>
          <Button>Refresh</Button>
          <Button kind="link">Open the record</Button>
          <Button kind="danger">Archive</Button>
          <Button disabled>Refresh</Button>
          <Button disabled><Spinner />Preparing</Button>
          <Button size="compact-sm">Show more</Button>
        </div>
      </section>

      <section>
        <h2>Action tiles</h2>
        <p className="muted">A few actions that each need a sentence to choose between. The title is the action, and the sentence fits two lines.</p>
        <ActionTiles label="Action tiles">
          <ActionTile icon={<IconClipboard />} title="Request manual enrichment" says="Adds this campaign to the manual enrichment queue on the Enrichment page." onClick={() => undefined} />
          <ActionTile icon={<IconNotes />} title="Add research notes" says="Tell whoever pulls from CoStar or ZoomInfo which submarkets, types or splits to use." onClick={() => undefined} />
          <ActionTile icon={<IconDownload />} title="Download the thin records" says="A CSV with EVIE ids for ZoomInfo. Run it there, then load the result on Import a file." onClick={() => undefined} />
        </ActionTiles>
        <ActionTiles label="Action tiles in their states">
          <ActionTile kind="orange" icon={<IconGlobe />} title="The orange kind" says="For the one tile that moves the work forward. At most one in a row." onClick={() => undefined} />
          <ActionTile icon={<IconGlobe />} title="Disabled" says="Grayed while a rule stops it. The rule is said in words above the row." onClick={() => undefined} disabled />
          <ActionTile icon={<IconGlobe />} title="Running" says={`${(412).toLocaleString()} of ${(1007).toLocaleString()} records looked up.`} onClick={() => undefined} busy />
          <ActionTile icon={<IconDownload />} title="Failed" says="The file was not made. The reason is said here, in the tile." tone="refusal" onClick={() => undefined} />
          <ActionTile title="No icon" says="Without an icon, the title starts at the left edge of the tile." onClick={() => undefined} />
        </ActionTiles>
      </section>

      <section>
        <h2>Inputs</h2>
        <div className="sg-row">
          <TextInput label="Search by name" id="sg-search" w="15rem" />
          <NativeSelect label="HubSpot" id="sg-hubspot" data={["any", "yes", "no", "not checked"]} />
          <TextInput label="State" id="sg-state" w="4.5rem" description="Two-letter code" />
          <Checkbox label="Automatic web enrichment" />
        </div>
      </section>

      <section>
        <h2>Status</h2>
        <div className="sg-row">
          {TONES.map((t) => <Badge key={t.tone} tone={t.tone}>{t.word}</Badge>)}
          <span className="chip">State: CA <button type="button" aria-label="Remove the filter State: CA">x</button></span>
        </div>
      </section>

      <section>
        <h2>Row actions</h2>
        <div className="sg-row">
          <RowMenu items={[
            { label: "Enrich", onSelect: () => undefined },
            { label: "Flag", onSelect: () => undefined },
            { label: "Archive", danger: true, onSelect: () => undefined },
          ]} />
        </div>
      </section>

      <section>
        <h2>Notices</h2>
        <div className="banner">Browser button 0.4.1 is ready. You have 0.4.0.</div>
        <div className="banner refusal" style={{ marginTop: ".4rem" }}>Web search has not answered since 2:10 PM. New pages wait until it does.</div>
      </section>

      <section>
        <h2>States</h2>
        <p className="muted"><Spinner />Loading.</p>
        <p className="muted"><Spinner />Refreshing.</p>
        <p className="refusal">No companies match these filters. Change them above, or press Refresh.</p>
        <p className="refusal" role="alert">The request failed and gave no reason. <Button size="compact-sm">Try again</Button></p>
      </section>

      <section>
        <h2>A table</h2>
        <p className="muted">Sort by a header on a wide screen, or from the control on a narrow one. The open row is marked. Narrow the window to see the rows change shape.</p>
        <DataTable
          columns={SAMPLE_COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          params={params}
          openId={open}
          onOpen={(r) => setOpen(r.id)}
          rowMenu={() => <RowMenu items={[{ label: "Enrich", onSelect: () => undefined }, { label: "Flag", onSelect: () => undefined }]} />}
        />
      </section>

      <section>
        <h2>A record</h2>
        <div className="sg-row">
          <Button onClick={() => setDrawer(true)}>Open a sample record</Button>
        </div>
        {drawer ? (
          <Drawer title="Example Residential" subtitle="Phoenix, AZ" onClose={() => setDrawer(false)}>
            <div className="fields">
              <Field label="Website"><a href="https://example.com">example.com</a></Field>
              <Field label="Properties">492</Field>
              <Field label="People">38</Field>
              <Field label="In HubSpot"><span className="muted" title="Not checked against HubSpot yet">-</span></Field>
            </div>
            <Section title="People here" count={2}>
              <table className="tight">
                <thead><tr><th>Name</th><th>Title</th><th>Reach</th></tr></thead>
                <tbody>
                  <tr><td>A Sample Person</td><td>Asset Manager</td><td className="muted">mobile, email</td></tr>
                  <tr><td>Another Sample Person</td><td>Director of Facilities</td><td className="muted">email</td></tr>
                </tbody>
              </table>
            </Section>
          </Drawer>
        ) : null}
      </section>
    </main>
  );
}
